import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Shell, MODES, type Mode } from './shell/Shell.js';
import { Palette } from './shell/Palette.js';
import { Rail } from './read/Rail.js';
import { Reader, type Register } from './read/Reader.js';
import { ancestorsOf, breadcrumb, navigableNodes, readerTarget } from './read/tree.js';
import { SignIn } from './shell/SignIn.js';
import { Account } from './shell/Account.js';
import { Dock } from './read/Dock.js';
import { Ledgers } from './ledgers/Ledgers.js';
import { Verify } from './verify/Verify.js';
import { Report } from './report/Report.js';
import { Compare } from './compare/Compare.js';
import { Protect } from './protect/Protect.js';
import { Ask } from './ask/Ask.js';
import { Calculators } from './calculators/Calculators.js';
import { DraftMonitor } from './drafts/DraftMonitor.js';
import { Room } from './room/Room.js';
import { Plain } from './plain/Plain.js';
import { Survey } from './survey/Survey.js';
import { Responses } from './survey/Responses.js';
import { ClaimWorkbench } from './claims/ClaimWorkbench.js';
import { QuickRef, type QuickRefEntry } from './shell/QuickRef.js';
import { api, type Identity, type TreeNode } from './lib/api.js';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60_000, refetchOnWindowFocus: false } },
});

/** `#/read/c/24.9.1(b)` — the mode and the clause both live in the URL. */
function readLocation(): { mode: Mode; clause: string | null } {
  const hash = window.location.hash.replace(/^#\//, '');
  const [modeSegment, ...rest] = hash.split('/');
  const mode = MODES.find((m) => m.toLowerCase() === (modeSegment ?? '').toLowerCase());
  const clause = rest[0] === 'c' && rest[1] ? decodeURIComponent(rest.slice(1).join('/')) : null;
  return { mode: mode ?? 'Read', clause };
}

/**
 * `#/plain` is the other reading level, and it renders without the shell.
 *
 * Not a mode: the mode nav, the rail and the dock are exactly what this
 * audience does not need. Two surfaces over one corpus, and both ship.
 */
/**
 * Which of the three top-level surfaces the hash names, as something React
 * re-renders on.
 *
 * `Gate` chooses between the public form, the plain surface and the workbench
 * by reading window.location.hash during render, which is only correct if
 * something re-renders it when the hash changes. Nothing did: the one
 * `hashchange` listener lives inside `Workbench` and calls `setMode`, which
 * re-renders Workbench and never the Gate above it. So a representative on the
 * Survey dashboard who pasted the public link into the same tab stayed on the
 * dashboard — the link right, the application ignoring it, which is worse than
 * a broken link because the obvious conclusion is that the address is wrong.
 * Anybody arriving fresh was always fine, so it failed only for the people
 * checking the link before sending it to a hundred and twenty others.
 *
 * The snapshot is the route rather than the hash, and the difference is the
 * whole fix. Subscribing to the hash itself re-renders Gate on every mode
 * change, and Workbench's effect that writes the hash back from its own state
 * then runs with a mode it has not updated yet — so `#/ledgers` was overwritten
 * with the view you had just left. Two browser tests caught it. Collapsing
 * every workbench hash to one value means moving between modes produces no
 * snapshot change and no re-render here, and only a genuine change of surface
 * does.
 */
function useTopLevelRoute(): string {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener('hashchange', onChange);
      return () => window.removeEventListener('hashchange', onChange);
    },
    () => {
      const slug = surveyFromHash();
      if (slug !== null) return `survey:${slug}`;
      return isPlainSurface() ? 'plain' : 'workbench';
    },
  );
}

/** `#/survey/staff-2026` — public, and rendered before the sign-in gate. */
function surveyFromHash(): string | null {
  const parts = window.location.hash.replace(/^#\//, '').split('/');
  return parts[0] === 'survey' && parts[1] ? decodeURIComponent(parts[1]) : null;
}

function isPlainSurface(): boolean {
  return window.location.hash.replace(/^#\//, '').split('/')[0] === 'plain';
}

function Workbench({ identity }: { identity: Identity }) {
  const initial = readLocation();
  const [mode, setMode] = useState<Mode>(initial.mode);
  const [selected, setSelected] = useState<string | null>(initial.clause);
  const [register, setRegister] = useState<Register>('Legal');
  const [heat, setHeat] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [quickRef, setQuickRef] = useState<QuickRefEntry | null>(null);
  /*
   * A passage carried from Read into Ask.
   *
   * State rather than the URL, because the hash here is written from state and
   * never read back after mount — setting it from a child would be overwritten
   * on the next render. Worth knowing before adding any other deep link.
   */
  const [askSeed, setAskSeed] = useState<string | null>(null);

  const tree = useQuery({ queryKey: ['tree'], queryFn: api.tree });
  const unpublished = useQuery({ queryKey: ['unpublished'], queryFn: api.unpublished });
  const nodes = useMemo<TreeNode[]>(() => tree.data ?? [], [tree.data]);

  // The outline drops lettered paragraphs: 435 of the 674 nodes are (a), (b),
  // (c) fragments, and listing them buried the clause titles among them. They
  // stay addressable and render inside the clause they belong to.
  const outline = useMemo(() => navigableNodes(nodes), [nodes]);

  // A citation may name a paragraph. The reader opens the clause around it and
  // the paragraph says which one it was, rather than stranding someone on a
  // page whose whole content is "Casual employment; or".
  const opened = useMemo(
    () => (selected ? readerTarget(nodes, selected) : null),
    [nodes, selected],
  );
  const focus = selected && opened !== selected ? selected : null;

  const clause = useQuery({
    queryKey: ['clause', opened],
    queryFn: () => api.clause(opened!),
    enabled: Boolean(opened),
  });

  // Selecting a clause opens the path to it, so a deep-linked citation is
  // never left hidden inside a collapsed Part.
  const select = useCallback(
    (number: string) => {
      setSelected(number);
      setExpanded((prev) => new Set([...prev, ...ancestorsOf(nodes, number)]));
    },
    [nodes],
  );

  /*
   * The URL follows the state, and only when it actually differs.
   *
   * The unconditional assignment pushed an identical hash on every render that
   * touched mode or selection, so the history stack filled with duplicates of
   * where you already were — and it is half of why Back did nothing useful.
   */
  useEffect(() => {
    const next = selected
      ? `#/${mode.toLowerCase()}/c/${encodeURIComponent(selected)}`
      : `#/${mode.toLowerCase()}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, [mode, selected]);

  /**
   * And the state follows the URL, which it never did.
   *
   * `readLocation()` ran once at mount and nothing listened afterwards, so the
   * address bar was write-only: the browser's Back and Forward buttons moved
   * the hash and the app ignored them, a pasted deep link did nothing unless
   * the page was reloaded, and a citation shared between representatives —
   * which is the whole point of putting the clause in the URL — opened
   * wherever the recipient happened to be.
   *
   * It also went unnoticed for a reason worth naming: every link inside the
   * app navigates by calling setMode and select, so the routes work perfectly
   * for anyone who never touches the browser's own controls.
   */
  useEffect(() => {
    const onNavigate = (): void => {
      const next = readLocation();
      setMode(next.mode);
      // Only when the URL names one. A mode with no clause segment is a
      // surface in its own right, and clearing the selection on every mode
      // change would lose the clause a person is working on.
      if (next.clause) setSelected(next.clause);
    };
    window.addEventListener('hashchange', onNavigate);
    return () => window.removeEventListener('hashchange', onNavigate);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Open the first Part once the tree arrives, so the rail never opens as a
  // wall of nine collapsed rows.
  useEffect(() => {
    if (nodes.length > 0 && expanded.size === 0 && !selected) {
      setExpanded(new Set([nodes[0]!.number]));
    }
  }, [nodes, expanded.size, selected]);

  const toggle = useCallback((number: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  }, []);

  if (tree.isError) {
    return <Notice title="Not loaded" body="The corpus could not be loaded. Is the API running?" />;
  }
  if (nodes.length === 0) return <Notice title="Loading the agreement" body="" />;

  return (
    <>
      <Shell
        mode={mode}
        onMode={setMode}
        onGuide={setQuickRef}
        who={identity.displayName}
        unpublished={unpublished.data?.unpublished ?? 0}
        onAccount={() => setAccountOpen(true)}
        onSignOut={() => {
          void api.signOut().then(() => window.location.reload());
        }}
        rail={
          mode === 'Read' ? (
            <Rail
              nodes={outline}
              selected={opened}
              onSelect={select}
              heat={heat}
              onHeat={setHeat}
              expanded={expanded}
              onToggle={toggle}
            />
          ) : undefined
        }
        canvas={
          mode === 'Claims' ? (
            <ClaimWorkbench
              isRep={identity.role === 'rep'}
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode === 'Ledgers' ? (
            <Ledgers
              isRep={identity.role === 'rep'}
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode === 'Verify' ? (
            // Reps only, and the route says so too. Showing a staffer an empty
            // queue would read as "nothing needs checking" rather than "this
            // is not yours", which is the wrong thing to teach about a screen
            // whose whole subject is whether something has been checked.
            identity.role === 'rep' ? (
              <Verify
                onOpenClause={(number) => {
                  setMode('Read');
                  select(number);
                }}
              />
            ) : (
              <Notice
                title="Checking is a representative's job"
                body="The comparisons here are drafts until a representative has read both clauses and signed them off."
              />
            )
          ) : mode === 'Room' ? (
            <Room
              isRep={identity.role === 'rep'}
              onOpenClaim={() => setMode('Claims')}
            />
          ) : mode === 'Ask' ? (
            <Ask
              seed={askSeed}
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode === 'Calculators' ? (
            <Calculators
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode === 'Drafts' ? (
            <DraftMonitor
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode === 'Survey' ? (
            /*
             * The slug is fixed rather than chosen. There is one survey per
             * bargaining round, and a picker over a list of one is a control
             * that exists to be got wrong.
             */
            <Responses slug="staff-2026" />
          ) : mode === 'Report' ? (
            <Report />
          ) : mode === 'Protect' ? (
            <Protect
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode === 'Compare' ? (
            <Compare
              selected={selected}
              onSelect={(number) => setSelected(number === '' ? null : number)}
              onOpenClause={(number) => {
                setMode('Read');
                select(number);
              }}
            />
          ) : mode !== 'Read' ? (
            <NotBuiltYet mode={mode} />
          ) : clause.data ? (
            <>
              <Breadcrumb nodes={nodes} number={clause.data.number} onSelect={select} />
              <Reader
                clause={clause.data}
                focus={focus}
                register={register}
                onRegister={setRegister}
                onSelect={select}
                onAsk={(seed) => {
                  setAskSeed(seed);
                  setMode('Ask');
                }}
                onCompare={(number) => {
                  select(number);
                  setMode('Compare');
                }}
              />
            </>
          ) : (
            <Opening count={nodes.length} />
          )
        }
        dock={
          mode === 'Read' && opened ? <Dock clause={opened} onSelect={select} /> : undefined
        }
      />
      {paletteOpen ? (
        <Palette
          nodes={outline}
          onClause={(number) => {
            setMode('Read');
            select(number);
          }}
          onMode={setMode}
          onClose={() => setPaletteOpen(false)}
        />
      ) : null}
      {accountOpen ? (
        <Account identity={identity} onClose={() => setAccountOpen(false)} />
      ) : null}
      {quickRef ? <QuickRef entry={quickRef} onClose={() => setQuickRef(null)} /> : null}
    </>
  );
}

function Breadcrumb({
  nodes,
  number,
  onSelect,
}: {
  nodes: TreeNode[];
  number: string;
  onSelect: (number: string) => void;
}) {
  const trail = breadcrumb(nodes, number);
  if (trail.length <= 1) return null;
  return (
    <nav
      className="flex flex-wrap items-baseline gap-1 border-b px-7 py-2"
      style={{ borderColor: 'var(--color-rule)' }}
      aria-label="Breadcrumb"
    >
      {trail.map((node, index) => (
        <span key={node.number} className="flex items-baseline gap-1">
          {index > 0 ? <span className="label">/</span> : null}
          <button
            onClick={() => onSelect(node.number)}
            className="clause-number text-[10px]"
            style={{ color: index === trail.length - 1 ? 'var(--color-ink)' : 'var(--color-muted)' }}
          >
            {node.number}
          </button>
        </span>
      ))}
    </nav>
  );
}

function NotBuiltYet({ mode }: { mode: Mode }) {
  // An empty state generates work rather than apologising.
  return (
    <div className="px-7 py-6">
      <p className="label">{mode}</p>
      <p className="register-reading measure mt-2" style={{ fontSize: 15 }}>
        Not built yet. Read works against the whole agreement — 674 clauses, every one checked word
        for word against the signed document.
      </p>
    </div>
  );
}

function Opening({ count }: { count: number }) {
  return (
    <div className="px-7 py-6">
      <p className="label">Read</p>
      <p className="register-reading measure mt-2" style={{ fontSize: 15 }}>
        {count} clauses, every one checked word for word against the signed agreement. Choose one
        from the outline, or press ⌘K and type a clause number.
      </p>
    </div>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-7 py-6">
      <p className="label">{title}</p>
      {body ? <p className="register-reading measure mt-2">{body}</p> : null}
    </div>
  );
}

/** The token on `#/sign-in?token=…`, if the visitor arrived by emailed link. */
function linkTokenFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#/sign-in')) return null;
  const query = hash.slice(hash.indexOf('?') + 1);
  return new URLSearchParams(query).get('token');
}

/**
 * Decides between the sign-in screen and the workbench.
 *
 * This gate is not what protects anything. It is what makes an identity
 * exist, and every row-level policy in the schema is written against that
 * identity — so the enforcement is in the database, and a mistake in this
 * component cannot widen what a request can read.
 *
 * The agreement text is the same document for everyone, but almost nothing
 * the workbench draws beside it is: badge counts, the claims on a clause and
 * who drafted a leverage block are all policy-guarded, so /tree and /clause
 * require an identity too. Only /instruments and /units, which are a list of
 * document titles and a table of published figures, are served without one.
 */
function Gate() {
  /*
   * The survey is answered before the gate, not behind it.
   *
   * Every other surface here needs an identity because every row it draws is
   * policy-guarded against one. This one is the opposite: a teacher follows a
   * link from a colleague and answers without an account, and the questions
   * are public by policy — `survey_read` is `using (true)`.
   *
   * The slug is read before the hooks and returned on after them, because a
   * hook cannot be skipped by an early return. `me` is disabled on this path
   * rather than merely ignored: a respondent with no session should not be
   * making an authentication call at all, and its 401 in the console is the
   * kind of thing that makes somebody close the tab.
   */
  // Subscribed first, so every branch below re-evaluates when the surface
  // changes — and only then.
  useTopLevelRoute();
  const surveySlug = surveyFromHash();
  const [linkToken] = useState(linkTokenFromHash);
  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
    enabled: surveySlug === null,
  });

  if (surveySlug !== null) return <Survey slug={surveySlug} />;

  if (me.isPending) return <Notice title="" body="" />;

  if (!me.data?.identity) {
    return (
      <SignIn
        linkToken={linkToken}
        onSignedIn={() => {
          void me.refetch();
        }}
      />
    );
  }

  return isPlainSurface() ? <Plain /> : <Workbench identity={me.data.identity} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Gate />
    </QueryClientProvider>
  );
}
